/**
 * Account feature-flag lookup. The add-in gates its UI on the account's AI
 * feature flags (the same flags the AI service enforces server-side):
 * `aiAgents` gates the whole add-in, `aiChecklist` gates the scan view.
 * The flags ride on the current-user endpoint with the account embedded,
 * mirroring ramp-web-app's getCurrentUser (actions/authActions.js):
 *   GET {API}/users/current/?embeds[]=accountId -> { ..., accountId: { features } }
 */

import { apiBaseUrl as API_BASE } from '@/shared/config/endpoints'

/** HTTP error carrying the status code so callers can detect auth failures. */
export class FeaturesHttpError extends Error {
  statusCode: number
  constructor(statusCode: number) {
    super(`Feature check failed (${statusCode}).`)
    this.name = 'FeaturesHttpError'
    this.statusCode = statusCode
  }
}

export interface AccountAiFeatures {
  aiAgents: boolean
  aiChecklist: boolean
}

interface CurrentUserResponse {
  accountId?: string | { features?: { aiAgents?: boolean; aiChecklist?: boolean } }
}

/**
 * The signed-in user's account AI feature flags. A flag is off only when the
 * populated account affirmatively lacks it; if the account failed to populate
 * the caller can't tell, so report everything enabled and let the backend's
 * own feature gates decide.
 */
export async function fetchAccountAiFeatures(token: string): Promise<AccountAiFeatures> {
  const res = await fetch(`${API_BASE}/users/current/?embeds[]=accountId`, {
    headers: { Accept: 'application/json', Authorization: token }
  })
  if (!res.ok) throw new FeaturesHttpError(res.status)

  const user = (await res.json()) as CurrentUserResponse
  const account = user?.accountId
  if (!account || typeof account !== 'object') {
    return { aiAgents: true, aiChecklist: true }
  }
  return {
    aiAgents: !!account.features?.aiAgents,
    aiChecklist: !!account.features?.aiChecklist
  }
}
